import { clsx } from "clsx";
import {
  type CSSProperties,
  forwardRef,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { mergeProps, useButton, useClipboard, useFocus, useHover } from "react-aria";

import { useDOMRef } from "../../hooks.ts";
import { DEPRECATED_useTooltip as useTooltip } from "../../hooks/DEPRECATED_useTooltip.ts";
import { Badge } from "../Badge/Badge.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Label } from "../Label/Label.tsx";
import { Shortcut } from "../Shortcut/Shortcut.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { TooltipIntent } from "../Tooltip/settings.ts";
import { Tooltip } from "../Tooltip/Tooltip.tsx";
import style from "./Copyable.module.css";

export interface CopyableProps {
  title?: (isSecure: boolean) => string;
  /** Asynchronous content getter. Will be run on mount only for unsecure context */
  getTextContent?: () => Promise<string>;
  /**
   * If you have `props.children` set, this has no effect.
   * If not it will preload the content to show it, except if it's `true`.
   * @default false
   */
  hideContent?: (isSecure: boolean) => boolean;
  /** @default false */
  isDisabled?: boolean;
  /**
   * This mode is selected automatically, preferring the Clipboard API by default (in https)
   * @default false
   */
  forceUnsecure?: boolean;
  className?: string;
  style?: CSSProperties;
  onCopy?: (content: string) => void;
}

export const Copyable = forwardRef<HTMLDivElement, PropsWithChildren<CopyableProps>>(function Copyable(props, ref) {
  const { forceUnsecure, isDisabled: isDisabledProp, onCopy: onCopyProp, getTextContent, hideContent } = props;

  const timeoutRef = useRef<number>(undefined);
  const isSecure = useMemo(() => window.isSecureContext && !!navigator.clipboard && !forceUnsecure, [forceUnsecure]);

  const [content, setContent] = useState<string>();

  const [isLoading, setLoading] = useState(false);
  const [isDisabled, setDisabled] = useState(isDisabledProp);

  const [isFocus, onFocusChange] = useState(false);
  const { focusProps } = useFocus({ onFocusChange, isDisabled });
  const { hoverProps, isHovered } = useHover({ isDisabled });

  const triggerRef = useDOMRef<HTMLDivElement>(ref);
  const { tooltipRef: copiedTooltipRef, ...copiedTooltipProps } = useCopyableTooltip(triggerRef, true, isDisabled);
  const { tooltipRef: warningTooltipRef, ...warningTooltipProps } = useCopyableTooltip(triggerRef, false, isDisabled);

  const onCopy = useCallback(
    (content: string) => {
      window.clearTimeout(timeoutRef.current);
      warningTooltipProps.state.close(true);
      copiedTooltipProps.setOpen!(true);
      onCopyProp?.(content);
      timeoutRef.current = window.setTimeout(() => {
        copiedTooltipProps.setOpen!(false);
      }, 1000);
    },
    [copiedTooltipProps.setOpen, warningTooltipProps.state, onCopyProp],
  );

  const onError = useCallback(() => {
    window.clearTimeout(timeoutRef.current);
    copiedTooltipProps.setOpen!(false);
    warningTooltipProps.state.open(true);
  }, [copiedTooltipProps.setOpen, warningTooltipProps.state]);

  const getContent = useCallback(async () => {
    setDisabled(true);
    setLoading(true);

    const content = await getTextContent?.();
    setContent(content);

    setDisabled(isDisabledProp ?? false);
    setLoading(false);
    return content;
  }, [getTextContent, isDisabledProp]);

  const copyProps = useAction(isSecure, content, getContent, onCopy, onError, triggerRef, isDisabled);

  useEffect(() => {
    // If it's loading, it's disabled disregarding the property value.
    if (!isLoading) setDisabled(isDisabledProp);
  }, [isDisabledProp, isLoading]);

  useEffect(() => {
    // Reset content if getContent changes.
    setContent(undefined);
  }, [getTextContent]);

  const shouldPreload = useMemo(() => {
    return !hideContent?.(isSecure) || !isSecure;
  }, [hideContent, isSecure]);

  useEffect(() => {
    // Preload content is case of an unsecure context. We preload probably a bit too much,
    // but it allows to update the content in an unsecure context when `getContent` changes.
    if (shouldPreload) {
      setDisabled(true);
      setLoading(true);

      getTextContent?.()
        .then((content) => {
          setContent(content);
          setDisabled(isDisabledProp ?? false);
          setLoading(false);
        })
        .catch((reason) => {
          if (import.meta.env.DEV) console.error("Copyable Error:", reason);
          setLoading(false);
        });
    }
  }, [shouldPreload, getTextContent, isDisabledProp]);

  return (
    <>
      <div
        {...(isDisabled ? {} : mergeProps(copyProps, hoverProps, focusProps))}
        role={isDisabled ? undefined : "textbox"}
        tabIndex={isDisabled ? -1 : 0}
        className={clsx(style.copyable, props.className, {
          [style.disabled]: isDisabled,
          [style.focus]: isFocus,
          [style.hover]: isHovered,
        })}
        style={props.style}
        ref={triggerRef}
        data-testid="copyable"
      >
        {!(hideContent?.(isSecure) && !props.title) && !props.children && (
          <VStack spacing={0}>
            {!!props.title && (
              <Label className={clsx({ [style.as_text]: !!hideContent })}>{props.title(isSecure)}</Label>
            )}
            {!hideContent?.(isSecure) && (
              <span className={clsx(style.content, { [style.as_text]: !props.title })}>{content ?? "..."}</span>
            )}
          </VStack>
        )}
        {props.children != undefined && (
          <VStack spacing={3}>
            {!!props.title && <Label>{props.title(isSecure)}</Label>}
            {props.children}
          </VStack>
        )}
        {isSecure ? (
          <SymbolIcon icon={"content_copy"} className={style.icon} />
        ) : (
          <Shortcut label={"Ctrl+C"} style={{ opacity: isDisabled ? 0.5 : undefined }} />
        )}
        {!!isLoading && <Badge label={"..."} className={style.badge} />}
      </div>
      {!!copiedTooltipProps.state.isOpen && (
        <Tooltip
          intent={TooltipIntent.Info}
          domProps={copiedTooltipProps.tooltipProps}
          ref={copiedTooltipRef}
          content={"Copied!"}
        />
      )}
      {!!warningTooltipProps.state.isOpen && (
        <Tooltip
          intent={TooltipIntent.Warning}
          domProps={warningTooltipProps.tooltipProps}
          ref={warningTooltipRef}
          title={"Something Went Wrong"}
          arrowProps={warningTooltipProps.arrowProps}
        >
          The content may have taken too long to load or the Clipboard functionality is currently blocked by the
          browser. Please try again or check the browser settings.
        </Tooltip>
      )}
    </>
  );
});

const useAction = (
  isSecure: boolean,
  content: string | undefined,
  getTextContent: () => Promise<string | undefined>,
  onCopy: (content: string) => void,
  onError: (reason: Error) => void,
  triggerRef: RefObject<HTMLElement | null>,
  isDisabled?: boolean,
) => {
  // Secure
  const { buttonProps } = useButton(
    {
      isDisabled,
      onPress: async () => {
        const _content = content ?? (await getTextContent());

        if (_content) {
          navigator.clipboard
            .writeText(_content)
            .then(() => {
              onCopy(_content);
            })
            .catch((reason) => {
              onError(reason);
              if (import.meta.env.DEV) {
                console.error("Copyable Error:", reason.name, reason.message);
              }
            });
        }
      },
    },
    triggerRef,
  );

  // Unsecure
  const { clipboardProps } = useClipboard({
    getItems() {
      if (isDisabled || !content) return [];
      onCopy(content);
      return [
        {
          "text/plain": content,
        },
      ];
    },
  });

  return isSecure ? buttonProps : clipboardProps;
};

const useCopyableTooltip = (triggerRef: RefObject<HTMLElement | null>, controlled: boolean, isDisabled = false) => {
  const [isOpen, setOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { state, tooltipProps, arrowProps } = useTooltip(triggerRef, tooltipRef, {
    isOpen: controlled ? isOpen : undefined,
    isDisabled,
    isDismissable: !controlled,
  });

  return {
    isOpen: controlled ? isOpen : undefined,
    setOpen: controlled ? setOpen : undefined,
    tooltipRef,
    state,
    tooltipProps,
    arrowProps,
  };
};
